import { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Save, Plus, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  SECTION_TYPE, DEFAULT_TITLES, DEFAULT_VARIANT, DEFAULT_VARIANT_BY_TYPE,
  TYPE_LABELS, TYPE_VARIANTS, ADDABLE_TYPES, variantsForSection,
} from "@/lib/sectionVariants";
import { ReelsSettings, DotsSettings, CarouselSettings } from "./SectionExtras";

// tiny CSS mock preview for each layout model
const VariantPreview = ({ variant }) => {
  const box = "bg-primary/25 rounded-[3px]";
  if (variant === "carousel") {
    return (
      <div className="flex gap-1 overflow-hidden w-20 h-12 items-center">
        <div className={`${box} w-7 h-10 shrink-0`} /><div className={`${box} w-7 h-10 shrink-0`} /><div className={`${box} w-4 h-10 shrink-0`} />
      </div>
    );
  }
  if (variant === "grid" || variant === "image_only" || variant === "compact") {
    return (
      <div className="grid grid-cols-2 gap-1 w-20 h-12">
        {[0, 1, 2, 3].map((i) => <div key={i} className={box} />)}
      </div>
    );
  }
  if (variant === "grid2") {
    return <div className="grid grid-cols-2 gap-1 w-20 h-12">{[0, 1].map((i) => <div key={i} className={box} />)}</div>;
  }
  if (variant === "list") {
    return <div className="flex flex-col gap-1 w-20 h-12 justify-center">{[0, 1, 2].map((i) => <div key={i} className={`${box} h-3 w-full`} />)}</div>;
  }
  if (variant === "featured") {
    return <div className="flex gap-1 w-20 h-12"><div className={`${box} flex-1`} /><div className="flex flex-col gap-1 w-8">{[0, 1, 2].map((i) => <div key={i} className={`${box} flex-1`} />)}</div></div>;
  }
  if (variant === "horizontal") {
    return <div className="flex flex-col gap-1 w-20 h-12 justify-center">{[0, 1].map((i) => <div key={i} className="flex gap-1 h-4"><div className={`${box} w-5`} /><div className={`${box} flex-1`} /></div>)}</div>;
  }
  return <div className="w-20 h-12" />;
};

const SectionsManager = () => {
  const { reload } = useSite();
  const [sections, setSections] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [newSec, setNewSec] = useState({ type: "businesses", title: "", variant: "grid" });

  useEffect(() => {
    api.get("/site/config").then((r) => setSections(r.data.sections)).catch(() => {});
  }, []);

  const patch = (key, changes) => setSections((s) => s.map((x) => (x.key === key ? { ...x, ...changes } : x)));

  const move = (idx, dir) => {
    const arr = [...sections];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    setSections(arr.map((x, i) => ({ ...x, order: i + 1 })));
  };

  const removeSection = (key) => {
    if (!window.confirm("Remover esta seção? (apenas seções criadas por você podem ser removidas)")) return;
    setSections((s) => s.filter((x) => x.key !== key));
  };

  const addSection = () => {
    const type = newSec.type;
    const key = `custom_${(crypto.randomUUID && crypto.randomUUID()) || Date.now()}`.slice(0, 40);
    const section = {
      key,
      type,
      label: TYPE_LABELS[type],
      title: newSec.title || TYPE_LABELS[type],
      variant: newSec.variant || DEFAULT_VARIANT_BY_TYPE[type] || "grid",
      enabled: true,
      custom: true,
      order: sections.length + 1,
    };
    setSections((s) => [...s, section]);
    setAddOpen(false);
    setNewSec({ type: "businesses", title: "", variant: "grid" });
    toast.success("Seção adicionada. Não esqueça de salvar.");
  };

  const save = async () => {
    try {
      await api.put("/admin/sections", { sections: sections.map((x, i) => ({ ...x, order: i + 1 })) });
      toast.success("Seções salvas");
      reload();
    } catch (e) {
      toast.error("Erro ao salvar");
    }
  };

  const selCls = "px-2 py-1.5 rounded-lg border border-border bg-background outline-none focus:border-primary text-sm";

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold font-heading text-slate-900">Seções / Layout da Página</h1>
          <p className="text-sm text-muted-foreground">Ative, renomeie, reordene e escolha o modelo de cada seção da página inicial</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setAddOpen(true)} data-testid="add-section-btn" className="flex items-center gap-2 border border-border rounded-full px-4 py-2.5 text-sm font-semibold hover:bg-slate-100 transition-colors">
            <Plus className="w-4 h-4" /> Adicionar nova seção
          </button>
          <button onClick={save} data-testid="save-sections-btn" className="flex items-center gap-2 bg-primary text-primary-foreground font-semibold rounded-full px-4 py-2.5 text-sm active:scale-95 transition-transform">
            <Save className="w-4 h-4" /> Salvar
          </button>
        </div>
      </div>

      {sections.some((x) => x.key === "banners") && (
        <DotsSettings section={sections.find((x) => x.key === "banners")} onChange={(c) => patch("banners", c)} />
      )}

      <div className="space-y-3">
        {sections.map((s, idx) => {
          const type = s.type || SECTION_TYPE[s.key];
          const isVisual = type !== "categories" && type !== "banners";
          const variants = variantsForSection(s);
          const currentVariant = s.variant || DEFAULT_VARIANT[s.key] || DEFAULT_VARIANT_BY_TYPE[type] || "grid";
          return (
            <div key={s.key} data-testid={`section-row-${s.key}`} className="bg-white rounded-2xl border border-border p-4">
              <div className="flex items-center gap-3">
                <span className="w-6 text-sm font-bold text-muted-foreground">{idx + 1}</span>
                <div className="flex-1 min-w-0">
                  <input
                    value={s.title ?? DEFAULT_TITLES[s.key] ?? s.label ?? ""}
                    onChange={(e) => patch(s.key, { title: e.target.value })}
                    data-testid={`title-${s.key}`}
                    className="w-full font-semibold text-slate-900 bg-transparent border border-transparent hover:border-border focus:border-primary rounded-lg px-2 py-1 outline-none"
                  />
                  <div className="text-xs px-2 flex items-center gap-2">
                    <span style={{ color: s.enabled ? "#22C55E" : "#94A3B8" }}>{s.enabled ? "ATIVADO" : "DESATIVADO"}</span>
                    <span className="text-muted-foreground">· {TYPE_LABELS[type] || type}</span>
                    {s.custom && <span className="text-primary font-semibold">· criada por você</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`move-up-${s.key}`} className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                  <button onClick={() => move(idx, 1)} disabled={idx === sections.length - 1} data-testid={`move-down-${s.key}`} className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                </div>
                {s.custom ? (
                  <button onClick={() => removeSection(s.key)} data-testid={`remove-${s.key}`} className="p-2 rounded-lg hover:bg-red-50 text-destructive"><Trash2 className="w-4 h-4" /></button>
                ) : (
                  <span className="w-8" />
                )}
                <Switch checked={s.enabled} onCheckedChange={() => patch(s.key, { enabled: !s.enabled })} data-testid={`toggle-${s.key}`} />
              </div>

              {isVisual && (
                <div className="mt-3 pl-9 flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Modelo:</span>
                    <select value={currentVariant} onChange={(e) => patch(s.key, { variant: e.target.value })} data-testid={`variant-${s.key}`} className={selCls}>
                      {variants.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                    </select>
                  </div>
                  <div className="border border-border rounded-xl p-2 bg-slate-50"><VariantPreview variant={currentVariant} /></div>
                </div>
              )}
              {type === "reels" && <ReelsSettings section={s} onChange={(c) => patch(s.key, c)} />}
              {(type === "categories" || type === "banners" || currentVariant === "carousel") && <CarouselSettings section={s} onChange={(c) => patch(s.key, c)} />}
            </div>
          );
        })}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Adicionar nova seção</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Escolha o tipo de conteúdo, o título e o modelo de layout da nova seção.</DialogDescription>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium mb-1 block">Tipo de conteúdo</label>
              <select
                value={newSec.type}
                onChange={(e) => setNewSec((p) => ({ ...p, type: e.target.value, variant: (TYPE_VARIANTS[e.target.value] || [])[0]?.value || "grid" }))}
                data-testid="new-section-type"
                className="w-full px-3 py-2 rounded-lg border border-border bg-background outline-none focus:border-primary text-sm"
              >
                {ADDABLE_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Título da seção</label>
              <input value={newSec.title} onChange={(e) => setNewSec((p) => ({ ...p, title: e.target.value }))} placeholder={TYPE_LABELS[newSec.type]} data-testid="new-section-title" className="w-full px-3 py-2 rounded-lg border border-border bg-background outline-none focus:border-primary text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Modelo de layout</label>
              <div className="flex items-center gap-3">
                <select value={newSec.variant} onChange={(e) => setNewSec((p) => ({ ...p, variant: e.target.value }))} data-testid="new-section-variant" className={`${selCls} flex-1`}>
                  {(TYPE_VARIANTS[newSec.type] || []).map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
                <div className="border border-border rounded-xl p-2 bg-slate-50"><VariantPreview variant={newSec.variant} /></div>
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-5">
            <button onClick={() => setAddOpen(false)} className="flex-1 border border-border rounded-full py-2.5 text-sm font-semibold hover:bg-slate-100 transition-colors">Cancelar</button>
            <button onClick={addSection} data-testid="confirm-add-section" className="flex-1 bg-primary text-primary-foreground rounded-full py-2.5 text-sm font-semibold active:scale-95 transition-transform">Adicionar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SectionsManager;
